import { Component, Ref } from "@eastsideco/salvo-ts";
import { ThemeComponent } from '@/theme/ThemeComponent';
import {
    elevate,
    type Autocomplete,
    type PhraseSuggestion,
    type ProductGroup
} from '@apptus/esales-api';
import { debounce } from "lodash";
import { formatMoney } from "@/utils/formatting";
import { resetElevateSessionIfTrackingConsentDenied } from "@/utils/elevate";

declare global {
    interface Window {
        elevate: ReturnType<typeof elevate>;
    }
}
@Component
export class SearchForm extends ThemeComponent  {
    @Ref searchForm!: HTMLInputElement;
    @Ref searchSubmit!: HTMLButtonElement;

    @Ref phraseSuggestionsContainer!: HTMLDivElement;
    @Ref phraseSuggestions!: HTMLDivElement;
    @Ref productsContainer!: HTMLDivElement;
    @Ref products!: HTMLDivElement;

    private result?: Autocomplete;

    get searchPerformed() {
        return this.result != undefined;
    }

    get hasResults() {
        if (!this.result) {
            return false;
        }

        return this.result?.productSuggestions.length > 0;
    }

    init() {
        this.$listen(this.searchForm, 'keydown', debounce((event: KeyboardEvent) => {
            if (event?.target == null) {
                return;
            }

            const target = event.target as HTMLInputElement;

            void this.onSearchInput(target.value);
        }, 50));
    }

    async onSearchInput(q: string) {
        if (q === "" || !window.elevate) {
            this.result = undefined;
            return;
        }

        resetElevateSessionIfTrackingConsentDenied();

        const result = await window.elevate.query.autocomplete({ q });

        this.result = result;

        this.showPhraseSuggestions();
        this.showProducts();
        this.updateSubmitText();
    }

    showResults() {
        this.searchForm.classList.remove('search-form__results---hidden');
    }

    showProducts() {
        const productsHTML = this.result?.productSuggestions.map((productGroup: ProductGroup) => {
            const [product] = productGroup.products;
            const isOnSale = product.sellingPrice.min != product.listPrice.min;

            const salePriceHTML = isOnSale ?
                `<del class="search-form__product__original-price">${formatMoney(product.listPrice.min * 100)}</del>` :
                '';

            return `
                <li class="search-form__product">
                    <a href="${product.link}" class="flex">
                        <div>
                            <img src="${product.imageInfo.thumbnail}" class="search-form__product__image" />
                        </div>

                        <div class="search-form__product__title">
                            <h3 class="search-form__product__title">${product.title}</h3>

                            <div class="search-form__product__prices">
                                <div 
                                    class="
                                        search-form__product__price 
                                        ${isOnSale ? 'search-form__product__price--on-sale' : ''}
                                    "
                                >
                                    ${formatMoney(product.sellingPrice.min * 100)}
                                </div>

                                ${salePriceHTML}
                            </div>
                        </div>
                    </a>
                </li>
            `;
        }).join('');

        if (! productsHTML) {
            this.productsContainer.classList.add('search-form__results__container--hidden');
            return;
        }

        this.products.innerHTML = `<ul>${productsHTML}</ul>`;
        this.productsContainer.classList.remove('search-form__results__container--hidden');
    }

    showPhraseSuggestions() {
        const suggestionsHTML = this.result?.phraseSuggestions.map(({ q, highlighted }: PhraseSuggestion) => `
                <li>
                    <a href="/pages/search-results-page?q=${encodeURI(q)}">
                        ${highlighted.replace('{', '<span>').replace('}', '</span>')}
                    </a>
                </li>
            `).join('');

        if (!suggestionsHTML) {
            this.phraseSuggestionsContainer.classList.add('search-form__results__container--hidden');
            return;
        }

        this.phraseSuggestionsContainer.classList.remove('search-form__results__container--hidden');
        this.phraseSuggestions.innerHTML = `<ul>${suggestionsHTML}</ul>`;
    }

    updateSubmitText() {
        if (!this.result) {
            return;
        }

        this.searchSubmit.innerText = `VIEW ALL ${this.result.totalHits} ITEMS`;
    }
}
